import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import CategoryBadge from "@/components/CategoryBadge";
import PhotoInput from "@/components/PhotoInput";
import PriceHint from "@/components/PriceHint";
import PrivacyBanner from "@/components/PrivacyBanner";
import { MapPin, Wallet, Calendar, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function MerchantRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, tCat, tCity, tAct } = useLang();
  const [req, setReq] = useState(null);
  const [myOffers, setMyOffers] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ price: "", delivery_time: "", notes: "", photo: null });

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/requests/${id}`),
        api.get(`/requests/${id}/offers`),
      ]);
      setReq(r1.data);
      setMyOffers(r2.data);
    } catch (e) {
      setErr(errMsg(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/offers", {
        request_id: id,
        price: parseFloat(form.price),
        delivery_time: form.delivery_time,
        notes: form.notes || null,
        photo: form.photo,
      });
      toast.success(t("offerSent"));
      navigate("/merchant/offers");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (err) return <div className="app-shell p-5 text-red-600">{err}</div>;
  if (!req) return <div className="app-shell p-5">…</div>;

  const existing = myOffers[0];
  const whatsappLink = (num) => `https://wa.me/${(num || "").replace(/[^0-9]/g, "")}`;

  return (
    <div className="app-shell pb-24" data-testid="merchant-request-detail">
      <TopBar title={t("requestDetails")} />
      <div className="px-5 pt-4">
        <div className="card-surface p-5">
          <div className="flex gap-3 items-start">
            <CategoryBadge category={req.category} size="lg" />
            <div className="flex-1 min-w-0">
              <div className="font-display text-xl font-bold">{tCat(req.category)}{req.activity ? ` · ${tAct(req.activity)}` : ""}</div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{tCity(req.city)}{req.district ? ` — ${req.district}` : ""}</span>
                <span className="inline-flex items-center gap-1"><Wallet className="h-3 w-3" />{req.budget} {t("saudiRiyal")}</span>
                <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{req.deadline}</span>
                {req.expired && <span className="pill bg-zinc-200 text-zinc-500" data-testid="merchant-expired-pill">{t("expired")}</span>}
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-zinc-700 whitespace-pre-wrap">{req.description}</p>
          {req.other_detail && (
            <div className="mt-3 rounded-xl bg-zinc-50 border border-zinc-200 px-3 py-2.5 text-sm" data-testid="merchant-request-other-detail">
              <span className="block text-[11px] font-bold text-zinc-500 mb-0.5">{t("otherHomeQuestion")}</span>
              <span className="text-zinc-800">{req.other_detail}</span>
            </div>
          )}
          {req.service_method && (
            <p className="mt-2 text-xs text-zinc-500" data-testid="merchant-request-service-method">{t("serviceMethod")}: {tAct(req.service_method)}</p>
          )}
          {req.photo && <img src={req.photo} alt="" className="mt-4 w-full rounded-xl object-cover max-h-72" />}
        </div>

        {!existing?.status || existing?.status !== "accepted" ? (
          <div className="mt-4"><PrivacyBanner variant="merchant" /></div>
        ) : null}

        {/* If accepted by customer and customer is this merchant's offer */}
        {existing?.status === "accepted" && req.customer?.phone && (
          <div className="mt-5 card-surface p-5 border-emerald-200 bg-emerald-50" data-testid="customer-contact-revealed">
            <p className="label-caps text-emerald-700">{t("youWonOrder")}</p>
            <div className="mt-2 font-semibold">{req.customer.name}</div>
            {req.customer.phone && (
              <div className="mt-1 text-sm inline-flex items-center gap-2"><Phone className="h-4 w-4" />{req.customer.phone}</div>
            )}
            {req.customer.phone && (
              <a
                href={whatsappLink(req.customer.phone)}
                target="_blank"
                rel="noreferrer"
                className="btn-primary w-full mt-3"
                data-testid="merchant-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4" />{t("contactWhatsApp")}
              </a>
            )}
          </div>
        )}

        {existing ? (
          <div className="mt-5 card-surface p-5" data-testid="existing-offer">
            <p className="label-caps">{t("myOffers")}</p>
            <div className="mt-2 flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-emerald-700">{existing.price} {t("saudiRiyal")}</div>
                <div className="text-sm text-zinc-600">{existing.delivery_time}</div>
              </div>
              <span className={`pill ${existing.status === "accepted" ? "bg-emerald-50 text-emerald-700" : existing.status === "rejected" ? "bg-zinc-100 text-zinc-500" : "bg-amber-50 text-amber-700"}`}>
                {t(existing.status)}
              </span>
            </div>
            {existing.notes && <p className="mt-2 text-sm text-zinc-700">{existing.notes}</p>}
          </div>
        ) : req.expired || req.status !== "open" ? (
          <div className="mt-5 card-surface p-5 text-sm text-zinc-600" data-testid="request-closed-note">
            {req.expired ? t("expired") : t(req.status)}
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-4">
            <h3 className="font-display text-lg font-semibold">{t("sendOffer")}</h3>
            <PriceHint
              requestId={id}
              currentPrice={form.price}
              onSuggest={(p) => setForm((f) => ({ ...f, price: String(p) }))}
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-caps mb-1.5 block">{t("price")}</label>
                <input
                  data-testid="offer-price"
                  type="number"
                  required
                  min="1"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full h-12 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3"
                />
              </div>
              <div>
                <label className="label-caps mb-1.5 block">{t("deliveryTime")}</label>
                <input
                  data-testid="offer-delivery"
                  required
                  placeholder={t("deliveryPlaceholder")}
                  value={form.delivery_time}
                  onChange={(e) => setForm({ ...form, delivery_time: e.target.value })}
                  className="w-full h-12 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3"
                />
              </div>
            </div>
            <div>
              <label className="label-caps mb-1.5 block">{t("notes")}</label>
              <textarea
                data-testid="offer-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3 py-2.5"
              />
            </div>
            <div>
              <label className="label-caps mb-2 block">{t("photoLabel")}</label>
              <PhotoInput value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} label={t("upload")} testId="offer-photo" />
            </div>
            <button data-testid="offer-submit" disabled={busy} className="btn-primary w-full">
              {busy ? "..." : t("sendOffer")}
            </button>
            <p className="text-xs text-zinc-500 text-center">{t("onlyAfterAccept")}</p>
          </form>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
